import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Check, X } from "lucide-react";

// Use environment variable with fallback to current origin
const API = window.location.origin;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_69566509-b9c0-48ae-b149-6b9accd67eee/artifacts/3nmi4pqp_Dynamic%20Logo%20with%20Sapphire%20Coral%20Gradient%20%288%29.png";

// Password strength checker
const checkPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(password)
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, passed, total: 5 };
};

const Login = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    rememberMe: false
  });

  const passwordStrength = checkPasswordStrength(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleGoogleLogin = () => {
    setIsLoading(true);
    // Redirect to dashboard (which handles both auth and guest) so the AuthCallback can process the session
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleLogin = () => {
    // Apple Sign-In disabled until credentials are set up
    // Button is greyed out with "Coming Soon" label
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    setError("");
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    // Validate password strength for signup
    if (isSignUp) {
      if (passwordStrength.passed < 5) {
        setError("Please meet all password requirements");
        setIsLoading(false);
        return;
      }
      if (!passwordsMatch) {
        setError("Passwords do not match");
        setIsLoading(false);
        return;
      }
    }

    try {
      const endpoint = isSignUp ? "/api/auth/register" : "/api/auth/login";
      const payload = isSignUp 
        ? { 
            email: formData.email, 
            password: formData.password, 
            confirm_password: formData.confirmPassword,
            name: formData.name
          }
        : { 
            email: formData.email, 
            password: formData.password,
            remember_me: formData.rememberMe
          };

      const fullUrl = `${API}${endpoint}`;
      console.log("Auth request to:", fullUrl);
      
      const response = await axios.post(fullUrl, payload, { 
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log("Auth response:", response.data);
      
      if (response.data.success) {
        if (response.data.email_verification_required) {
          setSuccessMessage(response.data.message);
          // Still navigate to dashboard but show verification notice
          setTimeout(() => {
            navigate("/dashboard", { state: { user: response.data.user, needsVerification: true }, replace: true });
          }, 2000);
        } else {
          navigate("/dashboard", { state: { user: response.data.user }, replace: true });
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      let message = "Authentication failed. Please try again.";
      if (err.response?.data?.detail) {
        // Handle Pydantic validation errors
        if (Array.isArray(err.response.data.detail)) {
          message = err.response.data.detail.map(e => e.msg).join(". ");
        } else {
          message = err.response.data.detail;
        }
      } else if (err.message === "Network Error") {
        message = "Connection blocked. Please disable ad blockers or try incognito mode.";
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestContinue = () => {
    navigate("/dashboard", { replace: true });
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
      rememberMe: false
    });
    setError("");
    setSuccessMessage("");
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-6 py-12" 
      style={{ backgroundColor: 'var(--background-primary)' }}
    >
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="Shadesense.ai" 
              className="h-12 w-auto mx-auto mb-6"
            />
          </Link>
          <h1 className="text-h2 mb-2" style={{ color: 'var(--text-primary)' }}>
            {isSignUp ? "Create Account" : "Welcome back"}
          </h1>
          <p className="text-small text-secondary">
            {isSignUp ? "Sign up to save your color palettes" : "Sign in to continue"}
          </p>
        </div>

        {/* Login Card */}
        <div className="card-glass p-8">
          {/* Success Message */}
          {successMessage && (
            <div 
              className="mb-6 p-3 rounded-lg text-small flex items-center gap-2"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
            >
              <Check className="w-4 h-4" />
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div 
              className="mb-6 p-3 rounded-lg text-small"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626' }}
            >
              {error}
            </div>
          )}

          {/* Social Login Buttons */}
          <div className="space-y-3">
            {/* Google Login */}
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-3"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Apple Login - Coming Soon */}
            <button
              disabled={true}
              className="btn-secondary w-full flex items-center justify-center gap-3 opacity-50 cursor-not-allowed relative"
              title="Apple Sign-In coming soon"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Continue with Apple
              <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: 'var(--accent-purple)', color: 'white' }}>
                Soon
              </span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
            <span className="text-meta text-muted">or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {/* Name field - only for signup */}
            {isSignUp && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" style={{ pointerEvents: 'none' }} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  className="input"
                  style={{ paddingLeft: '3rem' }}
                  required={isSignUp}
                  minLength={1}
                  maxLength={100}
                />
              </div>
            )}
            
            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" style={{ pointerEvents: 'none' }} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email address"
                className="input"
                style={{ paddingLeft: '3rem' }}
                required
              />
            </div>
            
            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" style={{ pointerEvents: 'none' }} />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Password"
                className="input"
                style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted hover:text-primary transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Password Strength Indicator - only for signup */}
            {isSignUp && formData.password && (
              <div className="space-y-2 text-xs">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className="flex-1 h-1 rounded-full transition-colors"
                      style={{ 
                        backgroundColor: i < passwordStrength.passed 
                          ? passwordStrength.passed >= 5 ? '#22c55e' : passwordStrength.passed >= 3 ? '#eab308' : '#ef4444'
                          : 'var(--border-subtle)'
                      }}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries({
                    length: '8+ characters',
                    lowercase: 'Lowercase letter',
                    uppercase: 'Uppercase letter',
                    number: 'Number',
                    symbol: 'Symbol (!@#$...)'
                  }).map(([key, label]) => (
                    <div 
                      key={key}
                      className="flex items-center gap-1"
                      style={{ color: passwordStrength.checks[key] ? '#22c55e' : 'var(--text-muted)' }}
                    >
                      {passwordStrength.checks[key] ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password - only for signup */}
            {isSignUp && (
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" style={{ pointerEvents: 'none' }} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm password"
                  className="input"
                  style={{ paddingLeft: '3rem', paddingRight: '5rem' }}
                  required={isSignUp}
                  minLength={8}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {formData.confirmPassword && (
                    passwordsMatch ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <X className="w-5 h-5 text-red-500" />
                    )
                  )}
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-muted hover:text-primary transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Remember Me - only for login */}
            {!isSignUp && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-small text-secondary">Stay signed in for 30 days</span>
              </label>
            )}
            
            <button
              type="submit"
              disabled={isLoading || (isSignUp && (passwordStrength.passed < 5 || !passwordsMatch))}
              className="btn-secondary w-full"
            >
              {isLoading ? (
                <span className="animate-shimmer inline-block w-20 h-4 rounded"></span>
              ) : (
                isSignUp ? "Create Account" : "Sign In"
              )}
            </button>
          </form>

          {/* Toggle Sign Up / Sign In */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                resetForm();
              }}
              className="text-small text-secondary hover:text-accent transition-colors"
            >
              {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
            </button>
          </div>

          {/* Guest Option Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
            <span className="text-meta text-muted">or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border-subtle)' }}></div>
          </div>

          {/* Continue as Guest */}
          <button
            onClick={handleGuestContinue}
            className="w-full flex items-center justify-center gap-2 py-3 text-small text-secondary hover:text-primary transition-colors"
          >
            Continue without account
            <ArrowRight className="w-4 h-4" />
          </button>

          {/* Terms */}
          <div className="mt-6 text-center">
            <p className="text-meta text-muted">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="text-secondary hover:text-accent transition-colors">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-secondary hover:text-accent transition-colors">Privacy Policy</Link>
            </p>
          </div>
        </div>

        {/* Footer Link */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-small text-secondary hover:text-primary transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
